<template>
	<div
		class="system-status-bar flex flex-wrap align-items-center justify-content-between column-gap-4 row-gap-2 px-3 py-2 border-bottom-1 surface-border surface-section"
	>
		<div class="flex flex-wrap align-items-center column-gap-3 row-gap-2">
			<span class="text-sm font-semibold text-color-secondary mr-1">System Status</span>
			<div
				v-for="item in subsystems"
				:key="item.key"
				class="flex align-items-center gap-2"
			>
				<span
					class="status-dot"
					:class="item.ok ? 'status-dot--ok' : 'status-dot--down'"
				></span>
				<span
					class="text-sm"
					:class="item.ok ? 'text-color' : 'text-red-500 font-medium'"
				>
					{{ item.label }}
				</span>
			</div>
			<span
				v-if="!allOk"
				class="text-xs font-semibold text-red-500 ml-1"
			>
				{{ alertSimulation ? 'Alert simulation' : 'ALERT' }}
			</span>
		</div>

		<div class="flex flex-wrap align-items-center column-gap-3 row-gap-1 text-sm">
			<span
				v-for="reading in environment"
				:key="reading.label"
				class="text-color-secondary"
			>
				{{ reading.label }}
				<span class="text-color font-semibold ml-1">
					{{ reading.value }}{{ reading.unit }}
				</span>
			</span>
			<PButton
				:icon="alertMuted ? 'pi pi-volume-off' : 'pi pi-volume-up'"
				:severity="!allOk && !alertMuted ? 'danger' : 'secondary'"
				:class="{ 'alert-mute-btn--active': !allOk && !alertMuted }"
				rounded
				text
				size="small"
				v-tooltip.left="alertMuted ? 'Unmute status alerts' : 'Mute status alerts'"
				@click="toggleMute()"
			/>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import BB from '@/brokers/BEBroker'
import HealthCheckBroker from '@/brokers/HealthCheckBroker'
import PB from '@/brokers/PersistencyBroker'
import Constants from '@/helpers/Constants'
import EventBus from '@/utils/EventBus'
import store from '@/store'
import { playAlertSound, silenceAlertSound, unlockAlertSound } from '@/utils/AlertSound'

const ALERT_REPEAT_MS = 1000

export default defineComponent({
	name: 'WSystemStatusBar',

	data: (): any => {
		return {
			intervalId: null,
			coreMdaOk: true,
			secondaryMdaOk: true,
			dcsOk: true,
			mainMetersOk: true,
			checkMetersOk: true,
			relativeHumidity: '—',
			ambientTemperature: '—',
			irradiance: '—',
			windSpeed: '—',
			alertMuted: false,
			alertSimulation: false,
			lastAlertAt: 0,
			wasAllOk: true,
			unlockHandler: null as null | (() => void),
		}
	},

	computed: {
		subsystems(): Array<{ key: string; label: string; ok: boolean }> {
			// Secondary MDA first: it is the only status path into PASS; when it
			// is down the backend forces every other flag offline as well.
			return [
				{ key: 'secondary', label: 'Secondary MDA', ok: this.secondaryMdaOk },
				{ key: 'core', label: 'Core MDA', ok: this.coreMdaOk },
				{ key: 'dcs', label: 'DCS', ok: this.dcsOk },
				{ key: 'main', label: 'Main Meters', ok: this.mainMetersOk },
				{ key: 'check', label: 'Check Meters', ok: this.checkMetersOk },
			]
		},
		environment(): Array<{ label: string; value: number | string; unit: string }> {
			const unit = (value: string, suffix: string) => (value === '—' ? '' : suffix)
			return [
				{ label: 'Humidity', value: this.relativeHumidity, unit: unit(this.relativeHumidity, '%') },
				{ label: 'Temp', value: this.ambientTemperature, unit: unit(this.ambientTemperature, '°C') },
				{ label: 'Irradiance', value: this.irradiance, unit: unit(this.irradiance, ' W/m²') },
				{ label: 'Wind', value: this.windSpeed, unit: unit(this.windSpeed, ' m/s') },
			]
		},
		allOk(): boolean {
			return this.subsystems.every((s: { key: string; label: string; ok: boolean }) => s.ok)
		},
	},

	mounted() {
		const muted = PB.get(Constants.LOCAL_STORAGE_IDS.STATUS_ALERT_MUTED)
		this.alertMuted = muted === 'true'
		this.unlockHandler = () => unlockAlertSound()
		window.addEventListener('pointerdown', this.unlockHandler, { once: true })
		EventBus.on(Constants.EVENTS.STATUS_ALERT_MUTE_CHANGED, this.onMuteChanged)
		EventBus.on(
			Constants.EVENTS.STATUS_ALERT_SIMULATION_CHANGED,
			this.onSimulationChanged,
		)
		this.startPolling()
	},

	beforeUnmount() {
		this.stopPolling()
		silenceAlertSound()
		EventBus.off(Constants.EVENTS.STATUS_ALERT_MUTE_CHANGED, this.onMuteChanged)
		EventBus.off(
			Constants.EVENTS.STATUS_ALERT_SIMULATION_CHANGED,
			this.onSimulationChanged,
		)
		if (this.unlockHandler) {
			window.removeEventListener('pointerdown', this.unlockHandler)
			this.unlockHandler = null
		}
	},

	methods: {
		startPolling() {
			this.stopPolling()
			this.refresh()
			this.intervalId = setInterval(() => {
				this.refresh()
			}, 2000)
		},

		stopPolling() {
			if (this.intervalId) {
				clearInterval(this.intervalId)
				this.intervalId = null
			}
		},

		async refresh() {
			try {
				const mdaHeartbeatData = await BB.getMdaHeartbeat()
				this.syncFlags(mdaHeartbeatData)
				this.syncDcsReadings(mdaHeartbeatData)
				await this.refreshSimulationFlag(mdaHeartbeatData)
			} catch {
				// Treat failed polls as all-down so a broken session/API is visible.
				this.coreMdaOk = false
				this.secondaryMdaOk = false
				this.dcsOk = false
				this.mainMetersOk = false
				this.checkMetersOk = false
				this.alertSimulation = false
				this.evaluateAlert()
			}
		},

		syncDcsReadings(flags: any) {
			const dcs = flags?.dcs_readings
			if (!dcs) {
				return
			}
			this.relativeHumidity = this.formatReading(dcs.humidity, 1)
			this.ambientTemperature = this.formatReading(dcs.ambient_temp, 1)
			this.irradiance = this.formatReading(dcs.irradiance, 1)
			this.windSpeed = this.formatReading(dcs.wind_speed, 1)
		},

		formatReading(value: unknown, digits = 1): string {
			if (value === null || value === undefined || value === '') {
				return '—'
			}
			const n = Number(value)
			if (!Number.isFinite(n)) {
				return '—'
			}
			return n.toFixed(digits)
		},

		async refreshSimulationFlag(flags: any) {
			// Prefer the health-check API when available (authoritative for overrides).
			if (store.getters.can('health_check', 'read')) {
				try {
					const status = await HealthCheckBroker.getStatus()
					this.alertSimulation = Boolean(
						status?.components?.some(
							(c: { simulated_offline?: boolean }) => c.simulated_offline,
						),
					)
					return
				} catch {
					// Fall through to heartbeat field.
				}
			}
			this.alertSimulation = Boolean(flags?.alert_simulation)
		},

		syncFlags(flags: any) {
			const now = new Date().getTime()
			if (!flags || now - parseInt(flags.ts) > 5000) {
				this.coreMdaOk = false
				this.secondaryMdaOk = false
				this.dcsOk = false
				this.mainMetersOk = false
				this.checkMetersOk = false
			} else {
				this.coreMdaOk = Boolean(flags.mda_core)
				this.secondaryMdaOk = Boolean(flags.mda_secondary)
				this.dcsOk = Boolean(flags.dcs)
				this.mainMetersOk = Boolean(flags.main_meters)
				this.checkMetersOk = Boolean(flags.check_meters)
			}
			this.evaluateAlert()
		},

		evaluateAlert() {
			const ok = this.allOk
			if (ok) {
				// Mute is a snooze for the current alert — clear it once healthy
				// so the next outage will sound again.
				if (this.alertMuted && !this.wasAllOk) {
					this.setMuted(false, { playIfAlerting: false })
				}
				this.wasAllOk = true
				this.lastAlertAt = 0
				return
			}

			const now = Date.now()
			const justFailed = this.wasAllOk
			const dueForRepeat = now - this.lastAlertAt >= ALERT_REPEAT_MS
			this.wasAllOk = false

			if (this.alertMuted) {
				return
			}
			if (justFailed || dueForRepeat || this.lastAlertAt === 0) {
				playAlertSound()
				this.lastAlertAt = now
			}
		},

		setMuted(muted: boolean, opts: { playIfAlerting?: boolean } = {}) {
			const playIfAlerting = opts.playIfAlerting !== false
			if (this.alertMuted === muted) {
				return
			}
			this.alertMuted = muted
			if (muted) {
				silenceAlertSound()
			} else {
				unlockAlertSound()
			}
			PB.set(
				Constants.LOCAL_STORAGE_IDS.STATUS_ALERT_MUTED,
				muted ? 'true' : 'false',
			)
			EventBus.emit(Constants.EVENTS.STATUS_ALERT_MUTE_CHANGED, muted)
			if (!muted && playIfAlerting && !this.allOk) {
				playAlertSound()
				this.lastAlertAt = Date.now()
			}
		},

		toggleMute() {
			this.setMuted(!this.alertMuted)
		},

		onMuteChanged(muted: unknown) {
			const nextMuted = Boolean(muted)
			if (nextMuted === this.alertMuted) {
				return
			}
			// External change (Settings) — apply without re-emitting.
			this.alertMuted = nextMuted
			if (nextMuted) {
				silenceAlertSound()
				return
			}
			unlockAlertSound()
			if (!this.allOk) {
				playAlertSound()
				this.lastAlertAt = Date.now()
			}
		},

		onSimulationChanged(simulating: unknown) {
			this.alertSimulation = Boolean(simulating)
		},
	},
})
</script>

<style scoped>
.status-dot {
	display: inline-block;
	width: 0.55rem;
	height: 0.55rem;
	border-radius: 50%;
	flex-shrink: 0;
}

.status-dot--ok {
	background: var(--green-500, #22c55e);
	box-shadow: 0 0 0 2px color-mix(in srgb, var(--green-500, #22c55e) 25%, transparent);
}

.status-dot--down {
	background: var(--red-500, #ef4444);
	box-shadow: 0 0 0 2px color-mix(in srgb, var(--red-500, #ef4444) 25%, transparent);
}

.alert-mute-btn--active {
	animation: status-alert-pulse 1.2s ease-in-out infinite;
}

@keyframes status-alert-pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.55;
	}
}
</style>
